
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import Toast from '@/utils/toast';
import CalendarContent from './CalendarContent.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import SelectComponent from '@/components/shared/Select.vue';
import WarningModal from '../shared/suites/WarningModal.vue';

const namespace: string = 'dashboard';

@Component({
	components: {
		CalendarContent,
		FullPageSpinner,
		SelectComponent,
		WarningModal
	}
})
export default class CalendarWidget extends Vue {
	@Action('updateRestaurant', { namespace: 'auth' }) private updateRestaurant!: (payload: { restaurant: Restaurant }) => Promise<void>;
	@Action('setCalendarType', { namespace }) private setCalendarType!: (calendarType: string) => void;
	@Action('setFilters', { namespace }) private setFilters!: (payload: DashboardFilters) => void;
	@Getter('getCalendarEvents', { namespace }) calendarEvents!: CalendarEvent[] | MonthlyCalendarEvent[];
	@Getter('getFilters', { namespace }) private filters!: DashboardFilters;
	@Getter('getCalendarType', { namespace }) private calendarType!: 'month' | 'week' | 'day';
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('areCateringEventsEnabled', { namespace: 'auth' }) private areCateringEventsEnabled!: boolean;
	@Getter('getMenus', { namespace: 'menus' }) menus!: Menu[];


	$refs!: {
		calendarContent: CalendarContent;
	};

	private warningModalProps: { title: string; message: string; onConfirm: () => void; } | null = null;
	private updatingClosedDate: boolean = false;

	private get selectedRange(): { start: string; end: string } {
		return { start: this.filters.startDate, end: this.filters.endDate };
	}

	private set selectedRange(range: { start: string; end: string }) {
		this.setFilters({ ...this.filters, startDate: range.start, endDate: range.end });
	}

	private get selectedStatuses(): string[] {
		return this.filters.status || [];
	}

	private set selectedStatuses(status: string[]) {
		this.setFilters({ ...this.filters, status });
	}

	private get selectedTypes(): string[] {
		return this.filters.type || [];
	}

	private set selectedTypes(type: string[]) {
		this.setFilters({ ...this.filters, type });
	}

	private get selectedStatusString(): string | null {
		if (this.selectedStatuses.length === 1) {
			return this.selectedStatuses[0].replace('-', '_');
		}
		return null;
	}

	private get selectedTypeString(): string | null {
		if (this.selectedTypes.length === 1) {
			return this.selectedTypes[0].replace('-', '_');
		}
		return null;
	}

	/**
	 * Get available order status options for filter dropdown
	 * K12 locations exclude pending status, since all orders are auto-accepted
	 *
	 * @return {{ text: string; value: string }[]}
	 */
	private get statusOptions(): { text: string; value: string }[] {
		const orderStatuses = [
			{ text: this.$t('orders.management.parent_card.order_card.pending'), value: 'pending' },
			{ text: this.$t('orders.management.parent_card.order_card.in_progress'), value: 'in-progress' },
			{ text: this.$t('orders.management.parent_card.order_card.completed'), value: 'completed' }
		];

		if (this.restaurant.k12) {
			orderStatuses.shift();
			return orderStatuses;
		}

		return orderStatuses;
	}

	/**
	 * Get available order type options for filter dropdown based on restaurant configuration
	 *
	 * @return {{ text: string; value: string }[]}
	 */
	private get typeOptions(): { text: string; value: string }[] {
		const hasScheduledMenus = this.menus.some((menu: Menu) => (menu.on_demand || menu.scheduled) && menu.published_at);
		const hasDineIn = this.menus.some((menu: Menu) => menu.dine_in && menu.published_at);
		const hasTakeOut = this.restaurant.catering
			? !!(this.restaurant.order_options?.generic_catering_show_pickup_option && hasScheduledMenus)
			: hasScheduledMenus;
		const hasSuites = this.restaurant.suites_enabled;
		const hasDelivery = !!(hasScheduledMenus && this.restaurant.delivery?.type);

		return [
			hasDineIn && { text: this.$t('orders.management.parent_card.header.dine_in_label'), value: 'dine-in' },
			hasTakeOut && { text: this.$t('orders.management.parent_card.header.pickup_label'), value: 'pickup' },
			hasDelivery && { text: this.$t('orders.management.parent_card.header.delivery_label'), value: 'delivery' },
			this.areCateringEventsEnabled && { text: this.$t('orders.management.parent_card.header.catering_event_label'), value: 'catering-event' },
			hasSuites && !this.areCateringEventsEnabled && { text: this.$t('orders.management.parent_card.header.catering_label'), value: 'catering' }
		].filter((option: false | { text: string, value: string }) => option) as { text: string, value: string }[];
	}

	/**
	 * Format current date range for display in header based on calendar type
	 *
	 * @return {string}
	 */
	private get formattedDateRange(): string {
		switch (this.calendarType) {
			case 'day':
				return DateTime.fromISO(this.selectedRange.start || '').setLocale(this.$i18n.locale).toFormat(this.$t('orders.dashboard.calendar.header_date_format'));
			case 'week':
				const start = DateTime.fromISO(this.selectedRange.start || '').setLocale(this.$i18n.locale).toFormat(this.$t('orders.dashboard.calendar.header_date_format_short'));
				const end = DateTime.fromISO(this.selectedRange.end || '').setLocale(this.$i18n.locale).toFormat(this.$t('orders.dashboard.calendar.header_date_format'));
				return `${start} - ${end}`;
			case 'month':
				return DateTime.fromISO(this.selectedRange.start).setLocale(this.$i18n.locale).toFormat('LLLL yyyy');
			default:
				return '';
		}
	}


	/**
	 * Navigate to previous date range based on current calendar type
	 * Updates both the selected range and instructs calendar view to navigate
	 *
	 * @return {void}
	 */
	private previousDateRange(): void {
		switch (this.calendarType) {
			case 'day':
				const prevDay = DateTime.fromISO(this.selectedRange.start).minus({ days: 1 });
				this.selectedRange = { start: prevDay.toISODate() || '', end: prevDay.toISODate() || '' };
				break;
			case 'week':
				const prevWeek = DateTime.fromISO(this.selectedRange.start).minus({ weeks: 1 });
				this.selectedRange = { start: prevWeek.startOf('week').toISODate() || '', end: prevWeek.endOf('week').toISODate() || '' };
				break;
			case 'month':
				const prevMonth = DateTime.fromISO(this.selectedRange.start).minus({ months: 1 });
				this.selectedRange = { start: prevMonth.startOf('month').toISODate() || '', end: prevMonth.endOf('month').toISODate() || '' };
				break;
		}
		this.$refs.calendarContent.prev(1);
	}

	/**
	 * Navigate to next date range based on current calendar type
	 * Updates both the selected range and instructs calendar view to navigate
	 *
	 * @return {void}
	 */
	private nextDateRange(): void {
		switch (this.calendarType) {
			case 'day':
				const nextDay = DateTime.fromISO(this.selectedRange.start).plus({ days: 1 });
				this.selectedRange = { start: nextDay.toISODate() || '', end: nextDay.toISODate() || '' };
				break;
			case 'week':
				const nextWeek = DateTime.fromISO(this.selectedRange.start).plus({ weeks: 1 });
				this.selectedRange = { start: nextWeek.startOf('week').toISODate() || '', end: nextWeek.endOf('week').toISODate() || '' };
				break;
			case 'month':
				const nextMonth = DateTime.fromISO(this.selectedRange.start).plus({ months: 1 });
				this.selectedRange = { start: nextMonth.startOf('month').toISODate() || '', end: nextMonth.endOf('month').toISODate() || '' };
				break;
		}
		this.$refs.calendarContent.next(1);
	}

	/**
	 * Handle calendar view type changes from either v-select dropdown or CalendarContent component
	 * Supports both string input (from v-select) and object input (from component events)
	 *
	 * @param {{ type: 'day' | 'week' | 'month', date?: string } | string} payload - New calendar type or change event
	 * @return {void}
	 */
	private handleCalendarTypeChange(payload: { type: 'day' | 'week' | 'month', date?: string } | string): void {
		// Handle change from v-select
		if (typeof payload === 'string') {
			const type = payload as 'day' | 'week' | 'month';
			const now = DateTime.now();

			// If the current range includes today, set date range to include today
			if (DateTime.fromISO(this.selectedRange.start) <= now && DateTime.fromISO(this.selectedRange.end) >= now) {
				this.selectedRange = {
					start: now.startOf(type).toISODate(),
					end: now.endOf(type).toISODate()
				};
			}
			else {
				this.selectedRange = {
					start: DateTime.fromISO(this.selectedRange.start).startOf(this.calendarType).startOf(type).toISODate(),
					end: DateTime.fromISO(this.selectedRange.start).endOf(type).toISODate()
				};
			}
			this.setCalendarType(type);
		}
		else {
			// Handle object input from CalendarContent component
			const { type, date } = payload;
			if (date) {
				this.selectedRange = {
					start: DateTime.fromISO(date).startOf('day').toISODate(),
					end: DateTime.fromISO(date).endOf('day').toISODate()
				};
			}
			else {
				this.selectedRange = {
					start: DateTime.fromISO(this.selectedRange.start).startOf(this.calendarType).startOf(type).toISODate(),
					end: DateTime.fromISO(this.selectedRange.start).endOf(type).toISODate()
				};
			}
			this.setCalendarType(type);
		}
		if (this.calendarType !== 'month') {
			this.$refs.calendarContent.scrollToTime();
		}
	}


	/**
	 * Update the restaurant data with updated closed dates
	 *
	 * @param {Restaurant} updatedRestaurant
	 * @return {Promise<void>}
	 */
	private async handleRestaurantUpdate(updatedRestaurant: Restaurant): Promise<void> {
		try {
			this.updatingClosedDate = true;
			await this.updateRestaurant({ restaurant: updatedRestaurant });
			this.warningModalProps = null;
		}
		catch (error) {
			Toast.error(this.$t('menu.editor.availability.holidays.error_message'));
		}
		finally {
			this.updatingClosedDate = false;
		}
	}

	/**
	 * Open modal to confirm adding or removing a closed date for the restaurant
	 * Prevents modification of dates in the past and updates restaurant holiday hours
	 *
	 * @param {'open' | 'close'} action
	 * @param {string} date
	 * @return {void}
	 */
	private openUpdateClosedDateModal(action: 'open' | 'close', date: string): void {
		if (DateTime.fromISO(date).startOf('day') < DateTime.now().setZone(this.restaurant.menu_configuration?.zone || 'America/Toronto').startOf('day')) {
			return;
		}
		const formattedDate = DateTime.fromISO(date).toFormat(this.$t('orders.dashboard.calendar.closed_date_modal.date_format'));
		if (action === 'close') {
			const updatedRestaurant = {
				...this.restaurant,
				holiday_hours: [...(this.restaurant.holiday_hours || []), { date, full_day: true, start_time: null, end_time: null }]
			};
			this.warningModalProps = {
				title: this.$t('orders.dashboard.calendar.closed_date_modal.assign_title'),
				message: this.$t('orders.dashboard.calendar.closed_date_modal.assign_message', { date: formattedDate }),
				onConfirm: () => this.handleRestaurantUpdate(updatedRestaurant)
			};
		}
		else {
			const updatedRestaurant = {
				...this.restaurant,
				holiday_hours: (this.restaurant.holiday_hours || []).filter(holiday => holiday.date !== date)
			};
			this.warningModalProps = {
				title: this.$t('orders.dashboard.calendar.closed_date_modal.remove_title'),
				message: this.$t('orders.dashboard.calendar.closed_date_modal.remove_message', { date: formattedDate }),
				onConfirm: () => this.handleRestaurantUpdate(updatedRestaurant)
			};
		}
	}

}
